import { ColumnType } from 'antd/lib/table'
import { useCallback } from 'react'
import DateRangeFilter from './components/DateRangeFilter'
import NumberRangeFilter from './components/NumberRangeFilter'
import SearchFilter from './components/SearchFilter'
import { useBooleanFilterItems } from './hooks/useBooleanFilterItems'
import { useFilterIcon } from './hooks/useFilterIcon'
import { useFilterItems } from './hooks/useFilterItems'
import { useOnFilter } from './hooks/useOnFilter'
import { FilterOptions, TableFilterItem } from './types'

/**
 * Creates a resolver that converts a {@link FilterOptions} configuration into
 * native Ant Design column filter options.
 *
 * The supplied records populate key-based and record-derived filter options.
 * An `undefined` collection is treated as an empty collection while records
 * are loading.
 *
 * @param records The records displayed by the table.
 * @returns A memoized resolver for one column's filter configuration.
 *
 * @example
 * ```tsx
 * const tableFilter = useTableFilter(records)
 *
 * const columns: ColumnsType<Project> = [
 *   {
 *     title: 'Name',
 *     dataIndex: 'name',
 *     ...tableFilter({ type: 'search', with: 'key', key: 'name' }),
 *   },
 * ]
 * ```
 */
export function useTableFilter<T extends object>(records: T[] | undefined) {
  const getFilterIcon = useFilterIcon<T>()
  const getOnFilter = useOnFilter<T>()
  const filterItems = useFilterItems()
  const booleanFilterItems = useBooleanFilterItems()

  return useCallback(
    (filter: FilterOptions<T>): ColumnType<T> => {
      const options: ColumnType<T> = {
        onFilter: getOnFilter(filter),
        filterIcon: getFilterIcon(filter),
      }

      if (filter.type === 'boolean') {
        options.filters = booleanFilterItems(filter.labels)
        if (filter.default !== undefined) {
          options.defaultFilteredValue = [String(filter.default)]
        }
      } else if (filter.type === 'checkboxes') {
        const checkboxes =
          typeof filter.items === 'function'
            ? filter.items(records ?? [])
            : filter.items
        options.filters = filterItems(
          checkboxes.map<TableFilterItem>(({ text, value, tooltip }) => {
            const labels =
              typeof filter.labels === 'function'
                ? filter.labels(value)
                : filter.labels
            const children = booleanFilterItems(labels, (checked) =>
              JSON.stringify({ value, checked })
            )
            return { text, value, tooltip, children }
          })
        )
      } else if (filter.type === 'date') {
        options.filterDropdown = (props) => <DateRangeFilter {...props} />
      } else if (filter.type === 'number') {
        options.filterDropdown = (props) => <NumberRangeFilter {...props} />
      } else if (filter.type === 'search') {
        options.filterDropdown = (props) => <SearchFilter {...props} />
      } else {
        let items
        if (filter.with === 'key')
          items = (records ?? [])
            .map((record) => record[filter.key] as string | number)
            .sort()
        else if (typeof filter.items === 'function')
          items = filter.items(records ?? [])
        else items = filter.items ?? []
        options.filters = filterItems(items)
      }

      return options
    },
    [booleanFilterItems, filterItems, getFilterIcon, getOnFilter, records]
  )
}
